import { useEffect, useMemo } from "react";

type KeyboardShortcutOptions = {
  key: string;
  callback: () => void;
  withShift?: boolean;
  withAltOption?: boolean;
  disabled?: boolean;
};

export const getIsMac = (): boolean =>
  typeof navigator !== "undefined" &&
  navigator.userAgent.toUpperCase().includes("MAC");

// Use this hook for keyboard shortcuts.
export const useKeyboardShortcut = ({
  key,
  callback,
  withShift = false,
  withAltOption = false,
  disabled = false,
}: KeyboardShortcutOptions) => {
  useEffect(() => {
    if (disabled) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const modifierKey = e.metaKey || e.ctrlKey; // Command for macOS, Ctrl for Windows
      const matchesKey = e.key && e.key.toLowerCase() === key.toLowerCase();

      // Handle Alt/Option
      const altOptionKey = e.altKey;

      // Early returns for conflicting modifiers
      if (!withShift && e.shiftKey) return;
      if (!withAltOption && altOptionKey) return;

      // Special case: both Shift and Alt/Option required but only one is pressed
      if (withShift && withAltOption && !(e.shiftKey && altOptionKey)) return;

      // Check if all required modifiers are pressed
      const shiftMatches = withShift ? e.shiftKey : true;
      const altOptionMatches = withAltOption ? altOptionKey : true;

      if (modifierKey && matchesKey && shiftMatches && altOptionMatches) {
        e.preventDefault();
        callback();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [key, callback, withShift, withAltOption, disabled]);
};

/** Whether the platform uses Command (Mac) vs Ctrl (Windows/Linux) as the primary modifier. */
export const useMetaKey = () => {
  const isMac = useMemo(() => getIsMac(), []);
  return { isMac };
};
